import { useMemo } from "react";

import { useGetFeaturesAPI } from "@/api/features/useFeaturesAPI";
import { useGetUserGroupByIdAPI } from "@/api/userGroups/useUserGroupsAPI";
import { SpinnerIcon } from "@/assets/icons";
import Modal from "@/components/ui/modal";
import { FeatureDetail } from "@/types/features";
import {
  AddGroupFormData,
  DeleteGroupPayload,
  EditGroupFormData,
  GetGroupByIdDetail,
  GetUserGroupsData,
  GroupPanelMode
} from "@/types/groups";

import AddGroupPanel from "./AddGroupPanel";
import EditGroupPanel from "./EditGroupPanel";
import ViewGroupPanel from "./ViewGroupPanel";

type SharedGroupPanelProps = {
  groupPanelMode: GroupPanelMode;
  setGroupPanelMode: (mode: GroupPanelMode) => void;
  isSubmitting: boolean;
  onClose: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onCreate: (data: AddGroupFormData) => void;
  onUpdate: (data: EditGroupFormData) => void;
  onDelete: (data: DeleteGroupPayload) => Promise<void>;
};

type GroupPanelProps = SharedGroupPanelProps & {
  groupData: GetUserGroupsData | null;
};

type GroupPanelContentProps = SharedGroupPanelProps & {
  featureList: FeatureDetail[] | null;
  groupDetailById: GetGroupByIdDetail | null;
};

const GroupPanelContent = ({
  groupPanelMode,
  setGroupPanelMode,
  isSubmitting,
  featureList,
  groupDetailById,
  onClose,
  onCreate,
  onUpdate,
  onDelete
}: GroupPanelContentProps) => {
  if (groupPanelMode === "view" && groupDetailById) {
    return (
      <ViewGroupPanel
        groupDetailById={groupDetailById}
        onDelete={onDelete}
        onClose={onClose}
        setGroupPanelMode={setGroupPanelMode}
      />
    );
  } else if (groupPanelMode === "edit" && groupDetailById) {
    return (
      <EditGroupPanel
        groupDetailById={groupDetailById}
        onClose={onClose}
        isSubmitting={isSubmitting}
        onSubmit={(payload) => onUpdate({ ...payload, id: groupDetailById.id })}
      />
    );
  } else if (groupPanelMode === "add") {
    return (
      <AddGroupPanel
        featureList={featureList ?? []}
        onClose={onClose}
        onSubmit={onCreate}
        isSubmitting={isSubmitting}
      />
    );
  }

  return null;
};

export const GroupPanel = ({
  groupPanelMode,
  setGroupPanelMode,
  groupData,
  isSubmitting,
  onDelete,
  onClose,
  onUpdate,
  onCreate
}: GroupPanelProps) => {
  const { data: groupDetailById, isLoading: isGetGroupLoading } =
    useGetUserGroupByIdAPI(groupData?.id ?? "", {
      enabled: !!groupPanelMode && !!groupData?.id
    });

  const { data: featureList, isLoading: isGetFeaturesLoading } =
    useGetFeaturesAPI(null, {
      enabled: groupPanelMode === "add"
    });

  const isLoading = useMemo(
    () => isGetGroupLoading || isGetFeaturesLoading,
    [isGetGroupLoading, isGetFeaturesLoading]
  );

  return (
    <Modal
      isOpen={!!groupPanelMode}
      onClose={onClose}
      shouldCloseModalOnClickOutside={groupPanelMode !== "view"}
      containerClassname="!static"
    >
      <div
        className="
        absolute top-4 right-4 z-[9] border-gainsboro bg-white rounded-lg w-[548px] h-[calc(100vh-32px)] overflow-y-auto
        flex flex-col"
      >
        {isLoading ? (
          <div className="flex flex-col flex-1 justify-center items-center">
            <SpinnerIcon
              className="spin-animation"
              data-testid="get-user-data-spinner"
              width={60}
              height={60}
            />
          </div>
        ) : (
          <GroupPanelContent
            groupPanelMode={groupPanelMode}
            setGroupPanelMode={setGroupPanelMode}
            featureList={featureList?.data ?? null}
            groupDetailById={groupDetailById?.data ?? null}
            isSubmitting={isSubmitting}
            onClose={onClose}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        )}
      </div>
    </Modal>
  );
};

export default GroupPanel;
